import { FieldPath, FieldValues, useController } from "react-hook-form";
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  FormControl,
  InputLabel,
} from "@mui/material";
import React from "react";
import { RhfInputProps } from "../../Forms/RhfInputProps";
import { useFetchCountries } from "../../Locations/useFetchCountries";
import { RHFFieldErrorText } from "../../Forms/RHFFieldErrorText";
import { TextFieldReadOnly } from "../../TransportNetwork/TTProtections/Form/AffectedSprs/MultiSelectMenues/TextFieldReadOnly";
import { textFieldDefaultProps } from "../../Forms/TextFieldDefaultProps";
import { isArrayUndefinedOrEmpty } from "../../Utils/arrays";
import { MuiSelectChip } from "../Select/MuiSelectChip";

type RhfCountryMultiSelectProps = {
  disabled?: boolean;
  label?: string;
};

export const RhfCountryMultiSelect = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  disabled,
}: RhfInputProps<TFieldValues, TName> & RhfCountryMultiSelectProps) => {
  const {
    field,
    fieldState: { error },
  } = useController({ control, name });

  const { data: countries } = useFetchCountries();
  const labels = countries
    ? Object.fromEntries(countries.map((country) => [country.Id, country.Name]))
    : {};

  return (
    <FormControl fullWidth size="small">
      {label && <InputLabel shrink>{label}</InputLabel>}
      <Autocomplete
        {...field}
        multiple
        noOptionsText="No options available"
        options={isArrayUndefinedOrEmpty(countries) ? [] : countries.map((c) => c.Id)}
        getOptionLabel={(optionId) => labels[optionId] ?? "NOT FOUND"}
        onChange={(_, selectedValues) => field.onChange(selectedValues)}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextFieldReadOnly {...params} {...textFieldDefaultProps} label={label} />
        )}
        renderTags={(values, getTagProps) =>
          values.map((value, index) => (
            <MuiSelectChip
              getTagProps={getTagProps}
              labels={labels}
              index={index}
              valueId={value}
            />
          ))
        }
        size="small"
        filterSelectedOptions
        disableCloseOnSelect
        disabled={disabled}
      />
      {!!error && <RHFFieldErrorText fieldError={error} />}
    </FormControl>
  );
};
